import { GA4Utilities } from './utils';

const UNKNOWN = 'Unknown';
const HIDDEN = 'Hidden';
const NA = 'Not Available';
const APPLICATION_TYPE = 'Legacy';
const APPLICATION_VERSION = '1';

// Utility functions to extract OS version from the userAgent
const getOSVersion = (userAgent) => {
  let osVersion = UNKNOWN;
  if (/Windows NT/.test(userAgent)) {
    const match = userAgent.match(/Windows NT (\d+\.\d+)/);
    osVersion = match ? match[1] : UNKNOWN;
  } else if (/Mac OS X/.test(userAgent)) {
    const match = userAgent.match(/Mac OS X (\d+_\d+)/);
    osVersion = (match && match[1]) ? match[1].replace('_', '.') : UNKNOWN;
  } else if (/Android/.test(userAgent)) {
    const match = userAgent.match(/Android (\d+\.\d+)/);
    osVersion = match ? match[1] : UNKNOWN;
  } else if (/CPU (iPhone )?OS/.test(userAgent)) {
    const match = userAgent.match(/CPU (iPhone )?OS (\d+_\d+)/);
    osVersion = (match && match[2]) ? match[2].replace('_', '.') : UNKNOWN;
  }
  return osVersion;
};

// Utility function to get the effective connection type
const getEffectiveConnectionType = (nav) => {
  if (nav && nav.connection && nav.connection.effectiveType) {
    return nav.connection.effectiveType;
  } else {
    return UNKNOWN;
  }
};

const preserveInConsole = (error) => {
  console.error(`Error Timestamp: ${new Date()}`);
  if (error instanceof Error) {
    console.error(`Error Name: ${error.name}`);
    console.error(`Error Message: ${error.message}`);
    console.error(`Error Stack Trace:\n${error.stack}`);
  } else {
    console.error(`Error Occured: ${String(error)}`);
  }
};

/**
 * Constructs the Haystack Frontend Log payload and sends it to the Lambda API.
 * @param error The error object to be logged.
 */
const pushHaystackFrontendLog = (error) => {
  try {
    const hasWinObj = typeof window !== 'undefined';
    const nav = hasWinObj ? window.navigator : null;
    const userAgent = nav ? nav.userAgent : '';
    const sessionObj = hasWinObj ? window.session : null;
    const gaClientId = GA4Utilities.getGAId();
    const hasAnalyticsData = !!sessionObj;

    // If the session object is available (hasAnalyticsData), consider consent accepted (UNKNOWN); otherwise, consider it declined (HIDDEN).
    const defaultValue = hasAnalyticsData ? UNKNOWN : HIDDEN;

    const meta = {
      ua_n: sessionObj && sessionObj.browser ? sessionObj.browser.browser : defaultValue,
      ua_v: sessionObj && sessionObj.browser ? String(sessionObj.browser.version) : defaultValue,
      ua_os: sessionObj && sessionObj.browser ? sessionObj.browser.os : defaultValue,
      ua_ov: hasAnalyticsData ? getOSVersion(userAgent) : defaultValue,
      u_c: (hasAnalyticsData && nav) ? getEffectiveConnectionType(nav) : defaultValue,
      u_dc: hasAnalyticsData ? (nav && nav.hardwareConcurrency) || 0 : 0,
      app_type: APPLICATION_TYPE,
      app_version: APPLICATION_VERSION,
      user_id: gaClientId
    };

    let errorInfo;

    if (error) {
      errorInfo = {
        msg: error.stringifiedError || String(error),
        err_msg: error.message || NA,
        err_type: error.name || NA,
        err_stack: error.stack || NA,
        c_err_lineNumber: error.lineNumber !== undefined ? String(error.lineNumber) : NA,
        c_err_colNumber: error.columnNumber !== undefined ? String(error.columnNumber) : NA,
        c_err_fileName: error.fileName || NA,
        c_err_functionName: NA,
        c_err_detail: 'Script',
        c_err_additional_properties: NA,
        c_err_source: error.source || 'LogFn',
        c_err_level: error.level || 'Debug'
      };
    }

    const eventDefaultProps = {
      e_ts: Date.now(),
      c_url: window.location.href,
      c_sessionObj: hasAnalyticsData ? JSON.stringify(sessionObj) : ''
    };

    const event = Object.assign(eventDefaultProps, errorInfo);

    const payload = {
      method: 'POST',
      body: JSON.stringify({ meta, events: [event] }),
      headers: { 'Content-Type': 'application/json' }
    };

    fetch('https://alfred.freshworks.com/v1/push-haystack-frontend-log', payload).catch((e) => {
      preserveInConsole(e);
    });
  } catch (e) {
    preserveInConsole(e);
  }
};

const handleClientError = (error) => {
  if (typeof window === 'undefined') {
    preserveInConsole(error);
    return;
  }
  pushHaystackFrontendLog(error);
};

const errorHandler = (message, fileName, lineNumber, columnNumber, error) => {
  try {
    const customError = {
      source: 'OnError',
      level: 'Debug',
      stringifiedError: error ? String(error) : '',
      message: typeof message === 'string' ? message : (error && error.message),
      lineNumber: lineNumber || (error && error.lineNumber),
      columnNumber: columnNumber || (error && error.columnNumber),
      fileName: fileName || (error && error.fileName)
    };

    if (error instanceof Error) {
      customError.name = error.name;
      customError.stack = error.stack;
    }
    handleClientError(customError);

    return false; // Returning true prevents the default browser error handling
  } catch (e) {
    preserveInConsole(e);
  }
};

const unhandledRejectionHandler = (event) => {
  try {
    const { reason } = event;
    const reasonString = reason ? String(reason) : '';

    const customError = {
      source: 'UnhandledRejection',
      level: 'Debug',
      stringifiedError: reasonString
    };

    if (reason instanceof Error) {
      customError.message = reason.message;
      customError.name = reason.name;
      customError.stack = reason.stack;
      customError.lineNumber = reason.lineNumber;
      customError.columnNumber = reason.columnNumber;
      customError.fileName = reason.fileName;
    } else {
      customError.message = reasonString;
    }

    // Known Error Patterns
    const pattern = /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/;

    const isFiltered = pattern.test(customError.message || '');
    if (isFiltered) {
      return;
    }

    handleClientError(customError);
  } catch (e) {
    preserveInConsole(e);
  }
};

const setupGlobalErrorHandlers = () => {
  window.onerror = errorHandler;
  window.addEventListener('unhandledrejection', unhandledRejectionHandler);
};

export { preserveInConsole, handleClientError, setupGlobalErrorHandlers };
